/**
 * TalentLens AI - Router Configuration
 * TanStack Router setup with protected routes
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  Outlet,
} from '@tanstack/react-router';
import { useAuthStore } from './store/auth';

// Lazy load pages
import Layout from './components/Layout';
import LoginPage from './pages/Login';
import DashboardPage from './pages/Dashboard';
import CVsPage from './pages/CVs';
import CVDetailPage from './pages/CVDetail';
import JobsPage from './pages/Jobs';
import JobDetailPage from './pages/JobDetail';
import SearchPage from './pages/Search';
import MatchPage from './pages/Match';
import PipelinePage from './pages/Pipeline';
import InterviewsPage from './pages/Interviews';
import AnalyticsPage from './pages/Analytics';
import AuditLogPage from './pages/AuditLog';
import ServerLogsPage from './pages/ServerLogs';

// Root route
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Auth check for protected routes
const authGuard = () => {
  const { isAuthenticated, isLoading } = useAuthStore.getState();
  if (!isLoading && !isAuthenticated) {
    throw redirect({ to: '/login' });
  }
};

// Login route (public)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState();
    if (isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
});

// Protected layout route
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: Layout,
  beforeLoad: authGuard,
});

// Dashboard
const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  component: DashboardPage,
});

// CVs
const cvsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/cvs',
  component: CVsPage,
});

const cvDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/cvs/$cvId',
  component: CVDetailPage,
});

// Jobs
const jobsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/jobs',
  component: JobsPage,
});

const jobDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/jobs/$jobId',
  component: JobDetailPage,
});

// Search
const searchRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/search',
  component: SearchPage,
});

// Match
const matchRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/match/$jobId',
  component: MatchPage,
});

// Pipeline
const pipelineRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/pipeline/$jobId',
  component: PipelinePage,
});

// Interviews
const interviewsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/interviews',
  component: InterviewsPage,
});

// Analytics
const analyticsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/analytics',
  component: AnalyticsPage,
});

// Audit Log
const auditLogRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/audit-log',
  component: AuditLogPage,
});

// Server Logs (admin only)
const serverLogsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/server-logs',
  component: ServerLogsPage,
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'admin') {
      throw redirect({ to: '/' });
    }
  },
});

// Build route tree
const routeTree = rootRoute.addChildren([
  loginRoute,
  layoutRoute.addChildren([
    dashboardRoute,
    cvsRoute,
    cvDetailRoute,
    jobsRoute,
    jobDetailRoute,
    searchRoute,
    matchRoute,
    pipelineRoute,
    interviewsRoute,
    analyticsRoute,
    auditLogRoute,
    serverLogsRoute,
  ]),
]);

// Create router
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

// Type registration
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default router;
